import { ToggleSwitch } from '@/components/buttons';
import { SelectField } from '@/components/fields';
import RangeField from '@/components/fields/RangeField';
import { ISelectFieldOption } from '@/components/fields/SelectField';
import { useEpvFamilies, useGLobalRegions, useProcessSteps } from '@/hooks/api';
import { useSliderRange } from '@/hooks/useSliderRange';
import { Button } from '@/styles/buttons';
import { SelectOption } from '@/styles/fields';
import {
	MatchingContainer,
	MatchingFilter,
	MatchingFilterHeader,
	MatchingFilterTitle,
	MatchingHeader,
} from '@/styles/matching';
import axios from '@/utils/axios';
import {
	CURRENT_YEAR,
	YEARS_TO_ADD,
	YEARS_TO_SUBTRACT,
	convertScenariosToList,
	currentYear,
	groupEpvFamilies,
	joinClassNames,
} from '@/utils/utils';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import MatchingCapacity from './MatchingCapacity';
import MatchingDelta from './MatchingDelta';
import MatchingSatisfaction from './MatchingSatisfaction';

interface MatchingProps {}

type TTab = 'satifaction' | 'delta' | 'capacity' | null;

const baseByOptions = [
	{
		label: 'Group',
		value: 'group',
	},
	{
		label: 'Global Regions',
		value: 'global regions',
	},
];

const Matching: React.FC<MatchingProps> = () => {
	const { data: tree } = useQuery(
		['capacity_tree'],
		() => axios.get<TCapacityTree>('/capacity_tree'),
		{
			select: ({ data }) => data,
		}
	);

	const [currentTab, setCurrentTab] = useState<TTab>(null);
	const [demandScenarioId, setDemandScenarioId] = useState<TValue>();
	const [capacityScenarioId, setCapacityScenarioId] = useState<TValue>();
	const [processStep, setProcessStep] = useState<TValue>();
	const [selectedEpvFamilies, setSelectedEpvFamilies] = useState<TValue[]>([]);
	const [demandRepartition, setDemandRepartition] = useState<TValue>('history');
	const [by, setBy] = useState<TValue>();
	const [regions, setRegions] = useState<TValue[]>([]);
	const [displays, setDisplays] = useState<TValue[]>([]);

	const [filterBySalesRegionOrEpv, setFilterBySalesRegionOrEpv] =
		useState(false);

	const { data: globalRegions } = useGLobalRegions();
	const { data: processSteps } = useProcessSteps();
	const { data: epvFamilies } = useEpvFamilies();

	const {
		value: yearRange,
		setValue: setYearRange,
		min,
		max,
	} = useSliderRange({
		currentValues: [CURRENT_YEAR, CURRENT_YEAR + YEARS_TO_ADD],
		min: Math.max(2019, CURRENT_YEAR - YEARS_TO_SUBTRACT),
		max: CURRENT_YEAR + YEARS_TO_ADD,
	});

	const [selectedRegions, setSelectedRegions] = useState<TValue[]>([]);
	const [selectedDemandRepartition, setSelectedDemandRepartition] =
		useState<TValue>();

	const byOptions: ISelectFieldOption[] =
		selectedDemandRepartition === 'history'
			? [
					...baseByOptions,
					{
						label: 'Production region',
						value: 'production region',
					},
					{
						label: 'Plant',
						value: 'plant',
					},
			  ]
			: baseByOptions;

	const { data: matchingData, refetch } = useQuery(
		['matching'],
		() =>
			axios.get<TMatchingData>('/get_balance', {
				paramsSerializer: (params) => {
					return qs.stringify(params, { arrayFormat: 'repeat' });
				},
				params: {
					demand_scenario_id: demandScenarioId,
					capacity_scenario_id: capacityScenarioId,
					year_start: yearRange[0],
					year_end: yearRange[1],
					matching_type: demandRepartition,
					global_regions: regions,
				},
			}),
		{
			enabled: false,
			select: ({ data }) => data,
			onSuccess: () => {
				setBy(-1);
				setSelectedRegions(regions);
				setSelectedDemandRepartition(demandRepartition);
			},
			cacheTime: 0,
		}
	);

	const { data: capacityScenarios } = useQuery(
		['capacity_scenarios'],
		() => axios.get<TScenario[]>('/capacity_scenarios'),
		{
			select: ({ data }) => convertScenariosToList(data),
		}
	);

	const { data: demandScenarios } = useQuery(
		['demand_scenarios'],
		() => axios.get<TScenario[]>('/demand_scenarios'),
		{
			select: ({ data }) => convertScenariosToList(data),
		}
	);

	const globalRegionsList =
		globalRegions?.map((region) => ({
			label: region,
			value: region,
		})) ?? [];

	const displayOptions: ISelectFieldOption[] = [
		'production region',
		'plant',
	].includes(by as string)
		? tree
			? [
					...new Set(
						tree
							.filter((node) =>
								selectedRegions.includes(node.global_region as string)
							)
							.map((node) =>
								by === 'plant' ? node.plant_code : node.production_region
							)
					),
			  ].map((node) => ({
					label: node,
					value: node,
			  }))
			: []
		: by === 'global regions'
		? selectedRegions.map((region) => ({
				label: region as string,
				value: region,
		  }))
		: [];

	const groupedEpvFamilies = groupEpvFamilies(epvFamilies ?? []);

	const selectedProcessStep = Object.entries(processSteps ?? {}).find(
		([key]) => key === processStep
	)?.[1];

	useEffect(() => {
		if (by === 'global regions') {
			setDisplays(selectedRegions);
		} else {
			setDisplays([]);
		}
	}, [by]);

	return (
		<div>
			<MatchingHeader>
				<SelectField
					options={demandScenarios ?? []}
					value={demandScenarioId}
					setValue={setDemandScenarioId}
					variant='secondary'
					title='Load a demand scenario'
				/>
				<SelectField
					options={capacityScenarios ?? []}
					value={capacityScenarioId}
					setValue={setCapacityScenarioId}
					variant='secondary'
					title='Load a capacity scenario'
				/>
			</MatchingHeader>
			<MatchingContainer>
				<MatchingFilter>
					<MatchingFilterTitle>Horizon</MatchingFilterTitle>
					<RangeField
						value={yearRange}
						min={min}
						max={max}
						setValueOnRelease
						onRelease={setYearRange}
						blockMinAt={currentYear()}
					/>
				</MatchingFilter>
				<MatchingFilter>
					<MatchingFilterTitle>Global Regions</MatchingFilterTitle>
					<SelectField
						options={globalRegionsList}
						multiple
						value={regions}
						setValue={setRegions}
						title='Select...'
						closeWhenSelect={false}
					/>
				</MatchingFilter>
				<MatchingFilter>
					<MatchingFilterTitle>Demand repartition</MatchingFilterTitle>
					<SelectField
						options={[
							{
								label: 'Current',
								value: 'history',
							},
							{
								label: 'By region',
								value: 'region',
							},
							{
								label: 'Throughout the group',
								value: 'group',
							},
						]}
						value={demandRepartition}
						setValue={setDemandRepartition}
						title='Select...'
					/>
				</MatchingFilter>
				<MatchingFilter></MatchingFilter>
				<MatchingFilter></MatchingFilter>
				<MatchingFilter>
					<Button
						disabled={
							!demandRepartition ||
							!demandScenarioId ||
							!capacityScenarioId ||
							regions.length === 0
						}
						onClick={() => refetch()}>
						Apply filters
					</Button>
				</MatchingFilter>
				<MatchingFilterHeader
					onClick={() => setCurrentTab('satifaction')}
					active={currentTab === 'satifaction'}>
					GRAPHIC SUMMARY
				</MatchingFilterHeader>
				<MatchingFilterHeader
					onClick={() => setCurrentTab('delta')}
					active={currentTab === 'delta'}>
					DELTA
				</MatchingFilterHeader>
				<MatchingFilterHeader
					onClick={() => setCurrentTab('capacity')}
					active={currentTab === 'capacity'}>
					TABLE SUMMARY
				</MatchingFilterHeader>
				<MatchingFilter>
					<MatchingFilterTitle>By</MatchingFilterTitle>
					<SelectField
						options={byOptions}
						value={by}
						setValue={setBy}
						title='Select...'
					/>
					{currentTab === 'capacity' && (
						<>
							<MatchingFilterTitle>Sales region</MatchingFilterTitle>
							<ToggleSwitch
								checked={filterBySalesRegionOrEpv}
								onToggle={async (checked) => {
									setFilterBySalesRegionOrEpv(checked);
									return checked;
								}}
							/>
							<MatchingFilterTitle>EPV</MatchingFilterTitle>
						</>
					)}
				</MatchingFilter>
				<MatchingFilter>
					<MatchingFilterTitle>Display</MatchingFilterTitle>
					<SelectField
						options={displayOptions}
						disabled={!displayOptions.length}
						value={displays}
						setValue={setDisplays}
						title='Select...'
						multiple
						closeWhenSelect={false}
						required
					/>
				</MatchingFilter>
				<MatchingFilter>
					<MatchingFilterTitle>Process Step</MatchingFilterTitle>
					<SelectField
						required
						options={Object.entries(processSteps ?? {}).map(([key, value]) => ({
							label: value.name,
							value: key,
						}))}
						value={processStep}
						setValue={setProcessStep}
						title='Select...'
					/>
					{selectedProcessStep?.showCapacitiesIfEpvFamilyFiltered && (
						<>
							<MatchingFilterTitle>EPV Families</MatchingFilterTitle>
							<SelectField
								multiple
								options={Object.entries(epvFamilies ?? {}).map(
									([key, value]) => ({
										label: value.epv_family_lvl2,
										value: value.epv_family_lvl2_id,
									})
								)}
								value={selectedEpvFamilies}
								setValue={setSelectedEpvFamilies}
								title='Select...'
								closeWhenSelect={false}
								customRender={({
									selectOption,
									isOptionSelected,
									setHighlightedIndex,
								}) =>
									groupedEpvFamilies?.map((epvFamily) => (
										<>
											<SelectOption
												key={`${epvFamily.label}-${epvFamily.value}-lvl1`}
												onClick={(e) => {
													e.stopPropagation();
													const childValues = epvFamily.children.map(
														(child) => child.value
													);
													if (
														childValues.every((child) =>
															selectedEpvFamilies.includes(child)
														)
													) {
														setSelectedEpvFamilies(
															selectedEpvFamilies.filter(
																(v) => !childValues.includes(v)
															)
														);
													} else {
														setSelectedEpvFamilies([
															...selectedEpvFamilies.filter(
																(v) => !childValues.includes(v)
															),
															...childValues,
														]);
													}
												}}
												className={joinClassNames(
													epvFamily.children
														.map((child) => child.value)
														.every((child) =>
															selectedEpvFamilies.includes(child)
														) && 'selected'
												)}
												onMouseEnter={() =>
													setHighlightedIndex(epvFamily.value as number)
												}>
												{epvFamily.label}
											</SelectOption>
											{epvFamily.children.length > 1 &&
												epvFamily.children?.map((child) => (
													<SelectOption
														style={{
															paddingLeft: '1.5rem',
														}}
														onMouseEnter={() => setHighlightedIndex(1)}
														className={joinClassNames(
															isOptionSelected(child) && 'selected'
															// highlightedIndex ===
															// 	parentIndex + childArray.length + childIndex &&
															// 	'highlighted'
														)}
														onClick={(e) => {
															e.stopPropagation();
															selectOption(child.value);
														}}>
														{child.label}
													</SelectOption>
												))}
										</>
									))
								}
							/>
						</>
					)}
				</MatchingFilter>
			</MatchingContainer>
			<div style={{ marginTop: '1rem' }}>
				{currentTab === 'satifaction' ? (
					<MatchingSatisfaction
						data={matchingData}
						yearRange={yearRange}
						processStep={processStep}
						by={by}
						displays={displays}
						selectedEpvFamilies={selectedEpvFamilies}
						selectedProcessStep={selectedProcessStep}
					/>
				) : currentTab === 'capacity' ? (
					<MatchingCapacity
						data={matchingData}
						yearRange={yearRange}
						processStep={processStep}
						regions={by === 'global regions' ? displays : selectedRegions}
						displays={displays}
						by={by}
						selectedEpvFamilies={selectedEpvFamilies}
						selectedProcessStep={selectedProcessStep}
						demandRepartition={demandRepartition}
						filterBySalesRegionOrEpv={filterBySalesRegionOrEpv}
					/>
				) : currentTab === 'delta' ? (
					<MatchingDelta
						data={matchingData}
						yearRange={yearRange}
						processStep={processStep}
						by={by}
						displays={displays}
						selectedEpvFamilies={selectedEpvFamilies}
						selectedProcessStep={selectedProcessStep}
					/>
				) : null}
			</div>
		</div>
	);
};

export default Matching;
